import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConflictResolutionDialog from './ConflictResolutionDialog';
import { conflictBridge } from '../../api/game-data/conflict-bridge';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { ConflictRequest, GameDataPayload, MergeSpec } from '../../types/game-data.types';

interface PendingConflict {
  request: ConflictRequest;
  resolve: (payload: GameDataPayload | null) => void;
}

/**
 * Мост «перехватчик 409 → React» со стороны React.
 *
 * Перехватчик живёт в axios и о дереве компонентов ничего не знает; провайдер регистрирует
 * себя в `conflictBridge` и показывает то, что перехватчик просит: диалог по разошедшимся
 * полям или ненавязчивый тост, если слить удалось молча.
 *
 * Монтируется один раз в `AdminLayout` — все ~20 таблиц игровых данных живут под ним и
 * ничего для этого не делают.
 */
const GameDataConflictProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const { showInfo } = useSnackbar();
  const [pending, setPending] = useState<PendingConflict | null>(null);

  // Колбэки живут в ref: регистрация в мосте должна пережить любой ре-рендер, иначе
  // ответ 409, прилетевший в этот момент, останется без обработчика.
  const showInfoRef = useRef(showInfo);
  showInfoRef.current = showInfo;
  const translateRef = useRef(t);
  translateRef.current = t;

  const resolve = useCallback(
    (request: ConflictRequest) =>
      new Promise<GameDataPayload | null>((resolvePromise) => {
        setPending((previous) => {
          // Диалог один. Если пока висел первый конфликт, прилетел второй — первый закрываем
          // отказом, иначе его запрос остался бы висеть навсегда с крутящейся кнопкой.
          previous?.resolve(null);
          return { request, resolve: resolvePromise };
        });
      }),
    [],
  );

  const notifyAutoMerged = useCallback((spec: MergeSpec<unknown>) => {
    showInfoRef.current(
      translateRef.current('gameData:conflict.autoMerged', {
        entity: translateRef.current(spec.entityKey),
      }) as string,
    );
  }, []);

  useEffect(() => conflictBridge.register({ resolve, notifyAutoMerged }), [resolve, notifyAutoMerged]);

  const finish = (payload: GameDataPayload | null) => {
    pending?.resolve(payload);
    setPending(null);
  };

  return (
    <>
      {children}
      <ConflictResolutionDialog
        request={pending?.request ?? null}
        onCancel={() => finish(null)}
        onSubmit={(payload) => finish(payload)}
      />
    </>
  );
};

export default GameDataConflictProvider;
